// src/container/Tienda/Tienda.jsx
import { useCallback, useEffect, useRef, useState } from "react";
import { PRODUCTOS } from "../../constants/productosTienda";
import {
  agregarAlCarrito,
  confirmarCarrito,
  eliminarDelCarrito,
  enviarMensajeChat,
  listarCarrito,
  listarChatCliente,
  loginCliente,
  logoutCliente,
  misPedidos,
  registrarCliente,
  restablecerPassword,
  sesionCliente,
  solicitarResetPassword,
} from "../../lib/tiendaApi";

const TALLAS = ["XS", "S", "M", "L", "XL", "XXL"];

const ESTADO_LABELS = {
  solicitado: "Solicitado",
  en_transito: "En tránsito",
  a_recoger: "A recoger",
  entregado: "Entregado",
};

const ESTADO_COLORS = {
  solicitado: "bg-amber-100 text-amber-800",
  en_transito: "bg-sky-100 text-sky-800",
  a_recoger: "bg-indigo-100 text-indigo-800",
  entregado: "bg-emerald-100 text-emerald-800",
};

const inputClasses =
  "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-200";

const labelClasses = "text-sm font-medium text-slate-700 mb-1 block";

// --- Recuperar contraseña ---

const ResetPasswordForm = ({ onDone, onCancel }) => {
  const [paso, setPaso] = useState("pedir"); // "pedir" | "restablecer"
  const [email, setEmail] = useState("");
  const [codigo, setCodigo] = useState("");
  const [password, setPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const pedirCodigo = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setErrorMsg("Escribe tu email.");
      return;
    }
    try {
      setSubmitting(true);
      setErrorMsg("");
      await solicitarResetPassword(email.trim());
      setPaso("restablecer");
    } catch (err) {
      setErrorMsg(err.message || "No se pudo enviar el código.");
    } finally {
      setSubmitting(false);
    }
  };

  const restablecer = async (e) => {
    e.preventDefault();
    if (!codigo.trim() || password.length < 6) {
      setErrorMsg("Revisa el código y que la contraseña tenga al menos 6 caracteres.");
      return;
    }
    try {
      setSubmitting(true);
      setErrorMsg("");
      await restablecerPassword(email.trim(), codigo.trim(), password);
      onDone();
    } catch (err) {
      setErrorMsg(err.message || "No se pudo cambiar la contraseña.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <article className="bg-white/90 rounded-2xl shadow-md border border-slate-100 p-6 max-w-md mx-auto">
      <h2 className="text-lg font-semibold text-slate-900 mb-3">Recuperar contraseña</h2>

      {paso === "pedir" ? (
        <form onSubmit={pedirCodigo} className="flex flex-col gap-3">
          <div>
            <label className={labelClasses}>Email</label>
            <input
              type="email"
              className={inputClasses}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tucorreo@ejemplo.com"
            />
          </div>

          {errorMsg && <p className="text-sm text-red-600">{errorMsg}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="mt-1 rounded-lg bg-indigo-600 text-white font-semibold py-2 text-sm hover:bg-indigo-700 transition-colors disabled:opacity-60"
          >
            {submitting ? "Enviando…" : "Enviar código"}
          </button>
          <button type="button" onClick={onCancel} className="text-sm text-slate-500 underline">
            Volver
          </button>
        </form>
      ) : (
        <form onSubmit={restablecer} className="flex flex-col gap-3">
          <p className="text-sm text-slate-500">
            Si tu email tiene cuenta, te ha llegado un código. Caduca en 10 minutos.
          </p>
          <div>
            <label className={labelClasses}>Código</label>
            <input
              type="text"
              className={inputClasses}
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              placeholder="Introduce el código"
            />
          </div>
          <div>
            <label className={labelClasses}>Contraseña nueva</label>
            <input
              type="password"
              className={inputClasses}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mínimo 6 caracteres"
            />
          </div>

          {errorMsg && <p className="text-sm text-red-600">{errorMsg}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="mt-1 rounded-lg bg-indigo-600 text-white font-semibold py-2 text-sm hover:bg-indigo-700 transition-colors disabled:opacity-60"
          >
            {submitting ? "Guardando…" : "Cambiar contraseña"}
          </button>
          <button type="button" onClick={onCancel} className="text-sm text-slate-500 underline">
            Volver
          </button>
        </form>
      )}
    </article>
  );
};

// --- Login / registro ---

const AuthForms = ({ onAuthenticated }) => {
  const [mode, setMode] = useState("login"); // "login" | "registro" | "reset"
  const [form, setForm] = useState({ nombre: "", email: "", telefono: "", password: "" });
  const [errorMsg, setErrorMsg] = useState("");
  const [avisoReset, setAvisoReset] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!form.email.trim() || !form.password.trim()) {
      setErrorMsg("Escribe tu email y contraseña.");
      return;
    }
    if (mode === "registro" && !form.nombre.trim()) {
      setErrorMsg("Escribe tu nombre.");
      return;
    }

    try {
      setSubmitting(true);
      const data =
        mode === "registro"
          ? await registrarCliente(form)
          : await loginCliente(form.email.trim(), form.password);
      onAuthenticated(data.nombre);
    } catch (err) {
      setErrorMsg(err.message || "No se pudo completar la operación.");
    } finally {
      setSubmitting(false);
    }
  };

  const aviso = avisoReset && (
    <p className="max-w-md mx-auto text-center text-sm text-emerald-300 mb-3">
      Contraseña cambiada. Ya puedes entrar con la nueva.
    </p>
  );

  if (mode === "reset") {
    return (
      <>
        {aviso}
        <ResetPasswordForm
          onDone={() => {
            setAvisoReset(true);
            setMode("login");
          }}
          onCancel={() => setMode("login")}
        />
      </>
    );
  }

  return (
    <>
      {aviso}
      <article className="bg-white/90 rounded-2xl shadow-md border border-slate-100 p-6 max-w-md mx-auto">
        <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={() => {
              setMode("login");
              setAvisoReset(false);
            }}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold ${
              mode === "login" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600"
            }`}
          >
            Entrar
          </button>
          <button
            type="button"
            onClick={() => setMode("registro")}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold ${
              mode === "registro" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600"
            }`}
          >
            Crear cuenta
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          {mode === "registro" && (
            <div>
              <label className={labelClasses}>Nombre</label>
              <input
                type="text"
                className={inputClasses}
                value={form.nombre}
                onChange={handleChange("nombre")}
                placeholder="Nombre y apellidos"
              />
            </div>
          )}

          <div>
            <label className={labelClasses}>Email</label>
            <input
              type="email"
              className={inputClasses}
              value={form.email}
              onChange={handleChange("email")}
              placeholder="tucorreo@ejemplo.com"
            />
          </div>

          {mode === "registro" && (
            <div>
              <label className={labelClasses}>Teléfono (opcional)</label>
              <input
                type="tel"
                className={inputClasses}
                value={form.telefono}
                onChange={handleChange("telefono")}
                placeholder="600 000 000"
              />
            </div>
          )}

          <div>
            <label className={labelClasses}>Contraseña</label>
            <input
              type="password"
              className={inputClasses}
              value={form.password}
              onChange={handleChange("password")}
              placeholder={mode === "registro" ? "Mínimo 6 caracteres" : ""}
            />
          </div>

          {errorMsg && <p className="text-sm text-red-600">{errorMsg}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="mt-1 rounded-lg bg-indigo-600 text-white font-semibold py-2 text-sm hover:bg-indigo-700 transition-colors disabled:opacity-60"
          >
            {submitting
              ? "Un momento…"
              : mode === "registro"
              ? "Crear cuenta"
              : "Entrar"}
          </button>

          {mode === "login" && (
            <button
              type="button"
              onClick={() => setMode("reset")}
              className="text-sm text-slate-500 underline text-center"
            >
              ¿Olvidaste tu contraseña?
            </button>
          )}
        </form>
      </article>
    </>
  );
};

// --- Tarjeta de artículo (visible sin sesión; añadir a la cesta exige sesión) ---

const ProductoCard = ({ producto, session, onAgregado, onIrALogin }) => {
  const [talla, setTalla] = useState("M");
  const [cantidad, setCantidad] = useState(1);
  const [errorMsg, setErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [añadido, setAñadido] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    try {
      setSubmitting(true);
      await agregarAlCarrito({
        articulo: producto.nombre,
        talla,
        cantidad: Number(cantidad) || 1,
      });
      setAñadido(true);
      setTimeout(() => setAñadido(false), 2000);
      onAgregado();
    } catch (err) {
      setErrorMsg(err.message || "No se pudo añadir a la cesta.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <article className="bg-white/90 rounded-2xl shadow-md border border-slate-100 overflow-hidden flex flex-col">
      <img
        src={producto.imagen}
        alt={producto.nombre}
        className="w-full h-56 object-contain bg-slate-50 p-3"
        loading="lazy"
        decoding="async"
      />

      <div className="p-4 flex flex-col gap-3 flex-1">
        <h3 className="font-semibold text-slate-900">{producto.nombre}</h3>

        {session ? (
          <form onSubmit={handleSubmit} className="flex flex-col gap-2 mt-auto">
            <div className="grid grid-cols-2 gap-2">
              <select
                className={inputClasses}
                value={talla}
                onChange={(e) => setTalla(e.target.value)}
              >
                {TALLAS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <input
                type="number"
                min={1}
                max={10}
                className={inputClasses}
                value={cantidad}
                onChange={(e) => setCantidad(e.target.value)}
              />
            </div>

            {errorMsg && <p className="text-xs text-red-600">{errorMsg}</p>}

            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg bg-indigo-600 text-white font-semibold py-2 text-sm hover:bg-indigo-700 transition-colors disabled:opacity-60"
            >
              {submitting ? "Añadiendo…" : añadido ? "Añadido ✓" : "Añadir a la cesta"}
            </button>
          </form>
        ) : (
          <button
            type="button"
            onClick={onIrALogin}
            className="mt-auto rounded-lg border border-indigo-200 text-indigo-700 font-semibold py-2 text-sm hover:bg-indigo-50 transition-colors"
          >
            Inicia sesión para reservar
          </button>
        )}
      </div>
    </article>
  );
};

const Catalogo = ({ session, onAgregado, onIrALogin }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    {PRODUCTOS.map((producto) => (
      <ProductoCard
        key={producto.id}
        producto={producto}
        session={session}
        onAgregado={onAgregado}
        onIrALogin={onIrALogin}
      />
    ))}
  </div>
);

// --- Mis pedidos ---

const MisPedidos = ({ pedidos }) => (
  <article className="bg-white/90 rounded-2xl shadow-md border border-slate-100 p-6">
    <h2 className="text-lg font-semibold text-slate-900 mb-4">Mis pedidos</h2>

    {pedidos.length === 0 ? (
      <p className="text-sm text-slate-500">Todavía no has confirmado ningún pedido.</p>
    ) : (
      <div className="flex flex-col gap-2">
        {pedidos.map((p) => (
          <div
            key={p.codigo}
            className="flex items-center justify-between gap-3 border border-slate-100 rounded-lg px-3 py-2 text-sm"
          >
            <div>
              <p className="font-semibold text-slate-900 tracking-wider">{p.codigo}</p>
              <p className="text-slate-700 text-xs mt-0.5">{p.articulo}</p>
              <p className="text-slate-500 text-xs">
                Talla {p.talla} · Cantidad {p.cantidad}
              </p>
            </div>
            <span
              className={`text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap ${ESTADO_COLORS[p.estado]}`}
            >
              {ESTADO_LABELS[p.estado] ?? p.estado}
            </span>
          </div>
        ))}
      </div>
    )}
  </article>
);

// --- Cesta flotante (asociada a la cuenta: vive en el servidor) ---

const CestaWidget = ({ refreshToken, onConfirmado }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [confirmando, setConfirmando] = useState(false);
  const [codigosConfirmados, setCodigosConfirmados] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const data = await listarCarrito();
      setItems(data.items || []);
    } catch (err) {
      setErrorMsg(err.message || "No se pudo cargar la cesta.");
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar, refreshToken]);

  const quitar = async (id) => {
    try {
      await eliminarDelCarrito(id);
      await cargar();
    } catch (err) {
      setErrorMsg(err.message || "No se pudo quitar el artículo.");
    }
  };

  const confirmar = async () => {
    try {
      setConfirmando(true);
      setErrorMsg("");
      const data = await confirmarCarrito();
      setCodigosConfirmados(data.pedidos || []);
      setItems([]);
      onConfirmado();
    } catch (err) {
      setErrorMsg(err.message || "No se pudo confirmar el pedido.");
    } finally {
      setConfirmando(false);
    }
  };

  const totalUnidades = items.reduce((acc, i) => acc + i.cantidad, 0);

  return (
    <>
      {open && (
        <div className="fixed bottom-24 left-6 z-50 w-80 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
          <header className="flex items-center justify-between px-4 py-3 bg-indigo-600 text-white">
            <span className="font-semibold text-sm">Tu cesta</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Cerrar cesta"
              className="text-white/80 hover:text-white text-lg leading-none"
            >
              ×
            </button>
          </header>

          <div className="p-3 max-h-80 overflow-y-auto">
            {codigosConfirmados ? (
              <div className="flex flex-col gap-2">
                <p className="text-sm text-slate-600">
                  ¡Pedido confirmado! Estos son tus códigos:
                </p>
                {codigosConfirmados.map((p) => (
                  <div
                    key={p.codigo}
                    className="rounded-lg bg-indigo-50 border border-indigo-100 p-2 text-center"
                  >
                    <p className="text-xs text-slate-600">
                      {p.articulo} · talla {p.talla} · cant. {p.cantidad}
                    </p>
                    <p className="text-lg font-bold tracking-widest text-indigo-700">
                      {p.codigo}
                    </p>
                  </div>
                ))}
                <p className="text-xs text-slate-400 mt-1">
                  Te lo hemos enviado también por email. Recuerda: no se
                  tramita nada hasta que vengas al club y pagues en persona.
                </p>
                <button
                  type="button"
                  onClick={() => setCodigosConfirmados(null)}
                  className="mt-2 text-sm text-indigo-600 underline"
                >
                  Seguir comprando
                </button>
              </div>
            ) : items.length === 0 ? (
              <p className="text-sm text-slate-400">Tu cesta está vacía.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between gap-2 border border-slate-100 rounded-lg px-3 py-2 text-sm"
                  >
                    <div>
                      <p className="font-medium text-slate-800">{item.articulo}</p>
                      <p className="text-xs text-slate-500">
                        Talla {item.talla} · Cantidad {item.cantidad}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => quitar(item.id)}
                      aria-label="Quitar de la cesta"
                      className="text-slate-400 hover:text-red-600 text-lg leading-none"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {errorMsg && <p className="text-xs text-red-600 px-3 pb-2">{errorMsg}</p>}

          {!codigosConfirmados && items.length > 0 && (
            <div className="p-3 border-t border-slate-100">
              <button
                type="button"
                onClick={confirmar}
                disabled={confirmando}
                className="w-full rounded-lg bg-indigo-600 text-white font-semibold py-2 text-sm hover:bg-indigo-700 transition-colors disabled:opacity-60"
              >
                {confirmando ? "Confirmando…" : "Confirmar pedido"}
              </button>
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Abrir cesta"
        className="fixed bottom-6 left-6 z-50 w-14 h-14 rounded-full bg-indigo-600 text-white shadow-xl flex items-center justify-center text-2xl hover:bg-indigo-700 transition-colors"
      >
        🛒
        {totalUnidades > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {totalUnidades}
          </span>
        )}
      </button>
    </>
  );
};

// --- Chat flotante ---

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [mensajes, setMensajes] = useState([]);
  const [texto, setTexto] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const cargar = useCallback(async () => {
    try {
      const data = await listarChatCliente();
      setMensajes(data.mensajes || []);
    } catch (err) {
      setErrorMsg(err.message || "No se pudo cargar el chat.");
    }
  }, []);

  useEffect(() => {
    if (open) cargar();
  }, [open, cargar]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "nearest" });
  }, [mensajes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!texto.trim()) return;

    try {
      setSending(true);
      setErrorMsg("");
      await enviarMensajeChat(texto.trim());
      setTexto("");
      await cargar();
    } catch (err) {
      setErrorMsg(err.message || "No se pudo enviar el mensaje.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-80 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
          <header className="flex items-center justify-between px-4 py-3 bg-indigo-600 text-white">
            <span className="font-semibold text-sm">Chat con el club</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Cerrar chat"
              className="text-white/80 hover:text-white text-lg leading-none"
            >
              ×
            </button>
          </header>

          <div className="flex flex-col gap-2 p-3 max-h-72 overflow-y-auto">
            {mensajes.length === 0 ? (
              <p className="text-sm text-slate-400">Aún no hay mensajes.</p>
            ) : (
              mensajes.map((m, i) => (
                <div
                  key={i}
                  className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${
                    m.autor === "cliente"
                      ? "self-end bg-indigo-600 text-white"
                      : "self-start bg-slate-100 text-slate-800"
                  }`}
                >
                  {m.mensaje}
                </div>
              ))
            )}
            <div ref={bottomRef} />
          </div>

          {errorMsg && <p className="text-xs text-red-600 px-3">{errorMsg}</p>}

          <form onSubmit={handleSubmit} className="flex gap-2 p-3 border-t border-slate-100">
            <input
              type="text"
              className={`${inputClasses} flex-1`}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Escribe tu mensaje…"
            />
            <button
              type="submit"
              disabled={sending}
              className="rounded-lg bg-indigo-600 text-white font-semibold px-4 text-sm hover:bg-indigo-700 transition-colors disabled:opacity-60"
            >
              Enviar
            </button>
          </form>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Abrir chat con el club"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-indigo-600 text-white shadow-xl flex items-center justify-center text-2xl hover:bg-indigo-700 transition-colors"
      >
        💬
      </button>
    </>
  );
};

// --- Contenedor principal ---

const Tienda = () => {
  const [session, setSession] = useState(null); // null = cargando, false = fuera, {nombre} = dentro
  const [pedidos, setPedidos] = useState([]);
  const [cestaRefreshToken, setCestaRefreshToken] = useState(0);
  const authRef = useRef(null);

  const cargarPedidos = useCallback(async () => {
    try {
      const data = await misPedidos();
      setPedidos(data.pedidos || []);
    } catch {
      setPedidos([]);
    }
  }, []);

  useEffect(() => {
    sesionCliente()
      .then((data) => setSession(data.logueado ? { nombre: data.nombre } : false))
      .catch(() => setSession(false));
  }, []);

  useEffect(() => {
    if (session) cargarPedidos();
  }, [session, cargarPedidos]);

  const handleLogout = async () => {
    await logoutCliente().catch(() => {});
    setSession(false);
    setPedidos([]);
  };

  const irALogin = () => {
    authRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (session === null) {
    return (
      <section className="bg-transparent">
        <div className="max-w-3xl mx-auto px-4 py-10 text-center text-slate-200 text-sm">
          Cargando…
        </div>
      </section>
    );
  }

  return (
    <section className="bg-transparent">
      <div className="max-w-3xl mx-auto px-4 py-6 md:py-10 flex flex-col gap-6">
        <header className="text-center">
          <h1
            className="text-2xl md:text-3xl font-bold"
            style={{ color: "var(--ua-gold)" }}
          >
            Tienda del club
          </h1>
          <p className="text-sm md:text-base text-slate-200 mt-2">
            Puedes ver los artículos sin iniciar sesión. Para añadirlos a la
            cesta necesitas una cuenta. El pedido no se tramita hasta que
            vienes al club y lo pagas en persona.
          </p>
        </header>

        <Catalogo
          session={session}
          onAgregado={() => setCestaRefreshToken((t) => t + 1)}
          onIrALogin={irALogin}
        />

        {!session ? (
          <div ref={authRef}>
            <AuthForms onAuthenticated={(nombre) => setSession({ nombre })} />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between max-w-md mx-auto w-full text-sm text-slate-200">
              <span>
                Hola, <strong>{session.nombre}</strong>
              </span>
              <button
                type="button"
                onClick={handleLogout}
                className="text-slate-300 underline hover:text-white"
              >
                Cerrar sesión
              </button>
            </div>

            <MisPedidos pedidos={pedidos} />
            <CestaWidget refreshToken={cestaRefreshToken} onConfirmado={cargarPedidos} />
            <ChatWidget />
          </>
        )}
      </div>
    </section>
  );
};

export default Tienda;
